import React, { useEffect, useState } from 'react';

import type { BulkEmailDTO } from '../../../services/apiClient';
import { useNavigate, useParams } from 'react-router-dom';

import Card from '../../../components/common/Card';
import BulkEmailForm from '../../../components/forms/BulkEmailForm';
import AdminLayout from '../../../components/layout/AdminLayout';
import { useBulkEmailApi } from '../../../hooks/useBulkEmailApi';

const BulkEmailEdit: React.FC = () => {
  const [initialValues, setInitialValues] = useState<BulkEmailDTO | null>({});
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getById, uploadBulkEmailAttachment, edit } = useBulkEmailApi();

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Bulk Emails', href: '/admin/bulkemails' },
    { label: 'Edit', href: '#' },
  ];

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        if (id) {
          const response = await getById(id);
          if (response) {
            setInitialValues(response.data || null);
          }
        }
      } catch (error) {
        console.error('Error fetching bulk email:', error);
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async (data: BulkEmailDTO): Promise<void> => {
    setIsSubmitting(true);
    try {
      let updatedValues = { ...data };

      if (attachmentFile) {
        const response = await uploadBulkEmailAttachment(
          attachmentFile.name,
          attachmentFile,
        );
        if (response && response.success && response.data) {
          updatedValues = { ...updatedValues, fileRecordId: response.data };
        }
      }

      // Custom list file is uploaded in the form; strip the sentinel value before saving
      if (updatedValues.communicationListId === '__custom_upload__') {
        updatedValues = { ...updatedValues, communicationListId: undefined };
      }

      if (updatedValues.id) {
        await edit(updatedValues.id, updatedValues);
        navigate('/admin/bulkemails');
      }
    } catch (error) {
      console.error('Error updating bulk email:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/bulkemails"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
    >
        {initialValues ? (
          <BulkEmailForm
            initialValues={initialValues}
            isSubmitting={isSubmitting}
            setInitialValues={setInitialValues}
            setAttachmentFile={setAttachmentFile}
            onSubmit={handleSubmit}
            onCancel={() => navigate('/admin/bulkemails')}
          />
        ) : (
          <p>Loading...</p>
        )}
    </AdminLayout>
  );
};

export default BulkEmailEdit;
